import { useMemo } from 'react';
import { Info, RotateCcw, AlertCircle, ChevronDown, Brain, Sparkles, Globe, Code, Image, Monitor, Search, Link } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import type { ModelParametersApi, ParameterConstraint, ReasoningEffort, ThinkingLevel, BuiltInToolConfig, BuiltInToolSettings } from '@/types';

interface ModelParameterFormProps {
  provider: 'anthropic' | 'openai' | 'google' | string;
  model: string;
  parameters: ModelParametersApi;
  constraints: Record<string, ParameterConstraint>;
  restrictions?: Record<string, boolean>;
  onChange: (params: ModelParametersApi) => void;
  onReset?: () => void;
}

// Provider별 파라미터 그룹 정의
const COMMON_PARAMS = ['temperature', 'max_tokens', 'timeout'];
const PROVIDER_PARAMS: Record<string, string[]> = {
  anthropic: ['top_p', 'top_k'],
  openai: ['top_p', 'frequency_penalty', 'presence_penalty', 'seed'],
  google: ['top_p', 'top_k', 'candidate_count'],
};

// Reasoning/Thinking 파라미터 (별도 섹션으로 표시)
const REASONING_PARAMS: Record<string, string[]> = {
  openai: ['reasoning_effort'],
  anthropic: ['thinking_budget_tokens'],
  google: ['thinking_level'],
};

// Enum 파라미터 값 정의
const REASONING_EFFORT_OPTIONS: { value: ReasoningEffort; label: string; description: string }[] = [
  { value: 'none', label: 'None', description: '추론 없음 (기본값)' },
  { value: 'low', label: 'Low', description: '가벼운 추론' },
  { value: 'medium', label: 'Medium', description: '보통 수준의 추론' },
  { value: 'high', label: 'High', description: '깊은 추론' },
  { value: 'xhigh', label: 'Extra High', description: '최대 수준의 추론' },
];

const THINKING_LEVEL_OPTIONS: { value: ThinkingLevel | 'none'; label: string; description: string }[] = [
  { value: 'none', label: '사용 안함', description: '사고 모드 비활성화' },
  { value: 'minimal', label: 'Minimal', description: '최소 수준의 사고' },
  { value: 'low', label: 'Low', description: '낮은 수준의 사고' },
  { value: 'high', label: 'High', description: '높은 수준의 사고' },
];

// 파라미터 라벨 및 설명
const PARAM_LABELS: Record<string, { label: string; labelKo: string }> = {
  temperature: { label: 'Temperature', labelKo: '온도' },
  max_tokens: { label: 'Max Tokens', labelKo: '최대 토큰' },
  timeout: { label: 'Timeout', labelKo: '타임아웃' },
  top_p: { label: 'Top P', labelKo: 'Top P (Nucleus)' },
  top_k: { label: 'Top K', labelKo: 'Top K' },
  frequency_penalty: { label: 'Frequency Penalty', labelKo: '빈도 패널티' },
  presence_penalty: { label: 'Presence Penalty', labelKo: '존재 패널티' },
  seed: { label: 'Seed', labelKo: '시드' },
  candidate_count: { label: 'Candidate Count', labelKo: '후보 수' },
  // Reasoning/Thinking 파라미터
  reasoning_effort: { label: 'Reasoning Effort', labelKo: '추론 강도' },
  thinking_budget_tokens: { label: 'Thinking Budget', labelKo: '사고 토큰 예산' },
  thinking_level: { label: 'Thinking Level', labelKo: '사고 수준' },
};

interface ParameterInputProps {
  name: string;
  value: number | null | undefined;
  constraint: ParameterConstraint | undefined;
  disabled?: boolean;
  onChange: (value: number | null) => void;
  onReset: () => void;
}

function ParameterInput({
  name,
  value,
  constraint,
  disabled,
  onChange,
  onReset,
}: ParameterInputProps) {
  const paramInfo = PARAM_LABELS[name] || { label: name, labelKo: name };
  const step = constraint?.step || (name === 'temperature' || name === 'top_p' ? 0.1 : 1);
  const defaultValue = constraint?.default;
  const isDisabled = disabled || constraint?.disabled;

  const displayValue = value ?? defaultValue ?? '';
  const isModified = value !== undefined && value !== null && value !== defaultValue;

  return (
    <div className={`space-y-2 p-3 rounded-lg border ${isDisabled ? 'bg-muted/50 opacity-60' : 'bg-background'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label htmlFor={name} className={`text-sm font-medium ${isDisabled ? 'text-muted-foreground' : ''}`}>
            {paramInfo.labelKo}
          </Label>
          {constraint?.description && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">{constraint.descriptionKo || constraint.description}</p>
                  {constraint.min !== undefined && constraint.max !== undefined && (
                    <p className="text-xs text-muted-foreground mt-1">
                      범위: {constraint.min} ~ {constraint.max}
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isDisabled && (
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
              <AlertCircle className="h-3 w-3 mr-1" />
              비활성
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isModified && (
            <Badge variant="secondary" className="text-xs">수정됨</Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={onReset}
            disabled={isDisabled || !isModified}
            title="기본값으로 복원"
          >
            <RotateCcw className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Input
          id={name}
          type="number"
          value={displayValue}
          onChange={(e) => {
            const val = e.target.value === '' ? null : parseFloat(e.target.value);
            onChange(val);
          }}
          min={constraint?.min}
          max={constraint?.max}
          step={step}
          disabled={isDisabled}
          className="w-full"
          placeholder={defaultValue !== null && defaultValue !== undefined ? `기본값: ${defaultValue}` : ''}
        />
      </div>

      {defaultValue !== null && defaultValue !== undefined && (
        <div className="text-xs text-muted-foreground">
          기본값: {defaultValue}
          {constraint?.min !== undefined && constraint?.max !== undefined && (
            <span className="ml-2">(범위: {constraint.min} ~ {constraint.max})</span>
          )}
        </div>
      )}
    </div>
  );
}

// Reasoning/Thinking 파라미터 입력 컴포넌트
interface ReasoningInputProps {
  provider: string;
  parameters: ModelParametersApi;
  constraints: Record<string, ParameterConstraint>;
  restrictions?: Record<string, boolean>;
  onChange: (params: ModelParametersApi) => void;
}

function ReasoningParameterSection({
  provider,
  parameters,
  constraints,
  restrictions = {},
  onChange,
}: ReasoningInputProps) {
  const reasoningParams = REASONING_PARAMS[provider] || [];

  if (reasoningParams.length === 0) return null;

  const handleEnumChange = (name: string, value: string) => {
    onChange({
      ...parameters,
      [name]: value === '' ? null : value,
    });
  };

  const handleNumberChange = (name: string, value: number | null) => {
    onChange({
      ...parameters,
      [name]: value,
    });
  };

  return (
    <Collapsible defaultOpen={true}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 text-sm font-semibold text-purple-700 hover:text-purple-900 transition-colors">
        <Brain className="h-4 w-4" />
        <span>Reasoning / Thinking 설정</span>
        <ChevronDown className="h-4 w-4 ml-auto" />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-2">
        {/* OpenAI reasoning_effort */}
        {provider === 'openai' && (
          <div className={`space-y-2 p-3 rounded-lg border ${restrictions.reasoning_effort ? 'bg-muted/50 opacity-60' : 'bg-purple-50 dark:bg-purple-950/20'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">
                  {PARAM_LABELS.reasoning_effort.labelKo}
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-xs">
                        {constraints.reasoning_effort?.descriptionKo || constraints.reasoning_effort?.description || '모델이 응답 생성 시 추론에 사용하는 연산량을 제어합니다.'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {parameters.reasoning_effort && parameters.reasoning_effort !== 'none' && (
                <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">수정됨</Badge>
              )}
            </div>
            <Select
              value={parameters.reasoning_effort || 'none'}
              onValueChange={(value) => handleEnumChange('reasoning_effort', value)}
              disabled={restrictions.reasoning_effort}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="추론 강도 선택" />
              </SelectTrigger>
              <SelectContent>
                {REASONING_EFFORT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      <span className="text-xs text-muted-foreground">{option.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground">
              gpt-5.2-chat-latest는 temperature 대신 reasoning_effort로 응답 품질을 조절합니다.
            </div>
          </div>
        )}

        {/* Anthropic thinking_budget_tokens */}
        {provider === 'anthropic' && (
          <div className={`space-y-2 p-3 rounded-lg border ${restrictions.thinking_budget_tokens ? 'bg-muted/50 opacity-60' : 'bg-amber-50 dark:bg-amber-950/20'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                <Label className="text-sm font-medium">
                  {PARAM_LABELS.thinking_budget_tokens.labelKo}
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-xs">
                        {constraints.thinking_budget_tokens?.descriptionKo || constraints.thinking_budget_tokens?.description || 'Claude가 내부 추론에 사용할 토큰 예산입니다.'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">범위: 1,024 ~ 128,000 토큰</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {parameters.thinking_budget_tokens && (
                <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">활성화</Badge>
              )}
            </div>
            <Input
              type="number"
              value={parameters.thinking_budget_tokens ?? ''}
              onChange={(e) => {
                const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                handleNumberChange('thinking_budget_tokens', val);
              }}
              min={1024}
              max={128000}
              step={1024}
              disabled={restrictions.thinking_budget_tokens}
              placeholder="설정 안함 (Extended Thinking 비활성화)"
              className="w-full"
            />
            <div className="text-xs text-muted-foreground">
              설정 시 Extended Thinking이 활성화됩니다. 비용이 증가할 수 있습니다.
            </div>
          </div>
        )}

        {/* Google thinking_level */}
        {provider === 'google' && (
          <div className={`space-y-2 p-3 rounded-lg border ${restrictions.thinking_level ? 'bg-muted/50 opacity-60' : 'bg-blue-50 dark:bg-blue-950/20'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-blue-500" />
                <Label className="text-sm font-medium">
                  {PARAM_LABELS.thinking_level.labelKo}
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-xs">
                        {constraints.thinking_level?.descriptionKo || constraints.thinking_level?.description || 'Gemini 3 모델의 사고 깊이를 제어합니다.'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {parameters.thinking_level && (
                <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">수정됨</Badge>
              )}
            </div>
            <Select
              value={parameters.thinking_level || 'none'}
              onValueChange={(value) => handleEnumChange('thinking_level', value === 'none' ? '' : value)}
              disabled={restrictions.thinking_level}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="사고 수준 선택" />
              </SelectTrigger>
              <SelectContent>
                {THINKING_LEVEL_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      <span className="text-xs text-muted-foreground">{option.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground">
              Gemini 3 Pro/Flash 모델에서 사용 가능합니다.
            </div>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function ModelParameterForm({
  provider,
  parameters,
  constraints,
  restrictions = {},
  onChange,
  onReset,
}: ModelParameterFormProps) {
  const providerParams = PROVIDER_PARAMS[provider] || [];

  // 파라미터 업데이트 핸들러
  const handleParamChange = (name: string, value: number | null) => {
    onChange({
      ...parameters,
      [name]: value,
    });
  };

  // 개별 파라미터 리셋
  const handleParamReset = (name: string) => {
    const defaultVal = constraints[name]?.default ?? null;
    onChange({
      ...parameters,
      [name]: defaultVal,
    });
  };

  // 모든 파라미터가 기본값인지 확인
  const hasModifiedParams = useMemo(() => {
    const allParams = [...COMMON_PARAMS, ...providerParams];
    return allParams.some((param) => {
      const value = parameters[param as keyof ModelParametersApi];
      const defaultVal = constraints[param]?.default;
      return value !== undefined && value !== null && value !== defaultVal;
    });
  }, [parameters, constraints, providerParams]);

  return (
    <div className="space-y-4">
      {/* 기본 파라미터 */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">기본 설정</h4>
          {onReset && hasModifiedParams && (
            <Button variant="ghost" size="sm" onClick={onReset} className="text-xs">
              <RotateCcw className="h-3 w-3 mr-1" />
              모두 초기화
            </Button>
          )}
        </div>
        {COMMON_PARAMS.map((param) => (
          <ParameterInput
            key={param}
            name={param}
            value={parameters[param as keyof ModelParametersApi] as number | null | undefined}
            constraint={constraints[param]}
            disabled={restrictions[param]}
            onChange={(value) => handleParamChange(param, value)}
            onReset={() => handleParamReset(param)}
          />
        ))}
      </div>

      {/* Provider별 고급 파라미터 */}
      {providerParams.length > 0 && (
        <Collapsible defaultOpen={false}>
          <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
            <ChevronDown className="h-4 w-4" />
            고급 설정 ({provider.charAt(0).toUpperCase() + provider.slice(1)})
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 pt-2">
            {providerParams.map((param) => (
              <ParameterInput
                key={param}
                name={param}
                value={parameters[param as keyof ModelParametersApi] as number | null | undefined}
                constraint={constraints[param]}
                disabled={restrictions[param]}
                onChange={(value) => handleParamChange(param, value)}
                onReset={() => handleParamReset(param)}
              />
            ))}

            {/* Temperature + Top P 경고 */}
            {(parameters.temperature !== undefined && parameters.temperature !== null &&
              parameters.top_p !== undefined && parameters.top_p !== null &&
              parameters.top_p !== 1.0) && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  <strong>참고:</strong> temperature와 top_p를 동시에 조정하면 예상치 못한 결과가 나올 수 있습니다.
                  일반적으로 둘 중 하나만 조정하는 것을 권장합니다.
                </p>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Reasoning/Thinking 파라미터 */}
      <ReasoningParameterSection
        provider={provider}
        parameters={parameters}
        constraints={constraints}
        restrictions={restrictions}
        onChange={onChange}
      />
    </div>
  );
}

// Built-in Tools Section Component
const TOOL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  web_search: Globe,
  google_search: Search,
  file_search: Search,
  code_interpreter: Code,
  code_execution: Code,
  image_generation: Image,
  computer_use: Monitor,
  url_context: Link,
};

const TOOL_COLORS: Record<string, string> = {
  web_search: 'text-blue-500',
  google_search: 'text-blue-500',
  file_search: 'text-green-500',
  code_interpreter: 'text-orange-500',
  code_execution: 'text-orange-500',
  image_generation: 'text-purple-500',
  computer_use: 'text-red-500',
  url_context: 'text-cyan-500',
};

interface BuiltInToolsSectionProps {
  provider: string;
  availableTools: Record<string, BuiltInToolConfig>;
  toolSettings: Record<string, BuiltInToolSettings>;
  onChange: (settings: Record<string, BuiltInToolSettings>) => void;
}

export function BuiltInToolsSection({
  provider,
  availableTools,
  toolSettings,
  onChange,
}: BuiltInToolsSectionProps) {
  const toolNames = Object.keys(availableTools);

  if (toolNames.length === 0) {
    return null;
  }

  const handleToolToggle = (toolName: string, enabled: boolean) => {
    onChange({
      ...toolSettings,
      [toolName]: {
        ...toolSettings[toolName],
        enabled,
      },
    });
  };

  const enabledToolsCount = Object.values(toolSettings).filter(s => s?.enabled).length;

  return (
    <Collapsible defaultOpen={enabledToolsCount > 0}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 text-sm font-semibold text-cyan-700 hover:text-cyan-900 transition-colors">
        <Globe className="h-4 w-4" />
        <span>내장 도구 설정</span>
        {enabledToolsCount > 0 && (
          <Badge variant="secondary" className="ml-2 text-xs bg-cyan-100 text-cyan-700">
            {enabledToolsCount}개 활성화
          </Badge>
        )}
        <ChevronDown className="h-4 w-4 ml-auto" />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-2">
        <div className="text-xs text-muted-foreground mb-3">
          {provider === 'openai' && '* 내장 도구는 OpenAI Responses API를 통해 활성화됩니다.'}
          {provider === 'anthropic' && '* Anthropic의 공식 도구를 활성화할 수 있습니다.'}
          {provider === 'google' && '* Google Grounding 및 코드 실행 도구를 활성화할 수 있습니다.'}
        </div>

        {toolNames.map((toolName) => {
          const tool = availableTools[toolName];
          const settings = toolSettings[toolName] || { enabled: false };
          const IconComponent = TOOL_ICONS[toolName] || Globe;
          const iconColor = TOOL_COLORS[toolName] || 'text-gray-500';

          return (
            <div
              key={toolName}
              className={`p-3 rounded-lg border ${settings.enabled ? 'bg-cyan-50/50 dark:bg-cyan-950/20 border-cyan-200 dark:border-cyan-800' : 'bg-background'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <IconComponent className={`h-5 w-5 ${iconColor}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{tool.name}</span>
                      {tool.costInfo && (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                          {tool.costInfo}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {tool.descriptionKo || tool.description}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => handleToolToggle(toolName, checked)}
                />
              </div>

              {/* 도구별 파라미터 (간소화) */}
              {settings.enabled && tool.parameters && Object.keys(tool.parameters).length > 0 && (
                <div className="mt-3 pt-3 border-t border-dashed">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground cursor-help">
                          <Info className="h-3 w-3" />
                          <span>추가 설정 가능</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-xs">
                          이 도구는 추가 파라미터 설정이 가능합니다. 기본값이 적용됩니다.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>
          );
        })}

        <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 rounded border border-amber-200 dark:border-amber-800">
          <strong>참고:</strong> 내장 도구 활성화 시 API 호출 비용이 증가할 수 있습니다.
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
